<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'EntityEvents',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    entityId: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const selectedEventId = ref(null);
  const eventData = ref(null);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noHeader: false,
    noPrimaryButton: true,
    size: 'full-screen',
  }));
  const b_7789867021953041_headerShowColumnSelectorButton = computed(
    () => false
  );
  const b_7789867021953041_headerShowReloadButton = computed(() => false);
  const b_7789867021953041_columns = computed(() => [
    {
      field: 'event_name',
      name: $fn.value.pgettext('EntityEvents', 'Event'),
      type: 'command',
    },
    {
      field: 'timestamp',
      name: $fn.value.pgettext('EntityEvents', 'Timestamp'),
      formatter: {
        name: 'datetime',
      },
    },
    {
      field: 'identity',
      name: $fn.value.pgettext('EntityEvents', 'User'),
      formatter: {
        name: 'user',
      },
    },
  ]);
  const b_2070266589801014_options = computed(() => ({
    lineNumbers: true,
    matchBrackets: true,
    continueComments: 'Enter',
  }));

  const $data = computed(() => ({
    selectedEventId: selectedEventId.value,
    eventData: eventData.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_7789867021953041_headerShowColumnSelectorButton:
      b_7789867021953041_headerShowColumnSelectorButton.value,
    b_7789867021953041_headerShowReloadButton:
      b_7789867021953041_headerShowReloadButton.value,
    b_7789867021953041_columns: b_7789867021953041_columns.value,
    b_2070266589801014_options: b_2070266589801014_options.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('list_entity_events', {
      parameters: { id: props.entityId },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedEventId' },
      { name: 'eventData' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      {
        name: 'b_7789867021953041_headerShowColumnSelectorButton',
        nodeId: '7789867021953041',
      },
      {
        name: 'b_7789867021953041_headerShowReloadButton',
        nodeId: '7789867021953041',
      },
      { name: 'b_7789867021953041_columns', nodeId: '7789867021953041' },
      { name: 'b_2070266589801014_options', nodeId: '2070266589801014' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedEventId,
      eventData,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_7789867021953041_headerShowColumnSelectorButton,
      b_7789867021953041_headerShowReloadButton,
      b_7789867021953041_columns,
      b_2070266589801014_options,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('EntityEvents/header', 'Entity events'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.entityId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('entityId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_e4vuflw49_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_7789867021953041_setData_l786tvliz_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedEventId',
      value: $event.data.data.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7789867021953041_setData_fip6tqf6z_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'eventData',
      value: JSON.stringify($event.data.response.data, null, 2),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7789867021953041_request_xpmanqbe6_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'read_entity_event_data',
      data: { id: props.entityId, event_id: selectedEventId.value },
    };
  };

  const act_7315092382398562_closeModal_e4vuflw49 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_e4vuflw49_getActionArgs,
    });
  const inst_evh_7315092382398562_close_dzisd8cs3 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_e4vuflw49,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_dzisd8cs3],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_fcnjbnucz =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_fx8oeyj9q =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_fcnjbnucz,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_fx8oeyj9q],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_tcjq55nk8 =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_gdugh5fgp =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_tcjq55nk8,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_gdugh5fgp],
    event: 'close',
  });
  const act_7789867021953041_setData_l786tvliz = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_l786tvliz_getActionArgs,
  });
  const inst_evh_7789867021953041_commandEventName_pwitilws5 =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_l786tvliz,
      event: 'command:event_name',
      displayName: 'setData',
    });
  const act_7789867021953041_setData_fip6tqf6z = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_fip6tqf6z_getActionArgs,
  });
  const inst_evh_7789867021953041_success_aifj42nun =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_fip6tqf6z,
      event: 'success',
      displayName: 'setData',
    });
  const act_7789867021953041_request_xpmanqbe6 = new actionTypes.RequestAction({
    actionArgs: act_7789867021953041_request_xpmanqbe6_getActionArgs,
    displayName: 'loadEventData',
    events: [inst_evh_7789867021953041_success_aifj42nun],
  });
  const inst_evh_7789867021953041_commandEventName_3q20sm0ak =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_request_xpmanqbe6,
      event: 'command:event_name',
      displayName: 'loadEventData',
    });
  const inst_evh_7789867021953041_commandEventName =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_7789867021953041_commandEventName_pwitilws5,
        inst_evh_7789867021953041_commandEventName_3q20sm0ak,
      ],
      event: 'command:event_name',
    });

  const actions = {
    inst_evh_7315092382398562_close_dzisd8cs3,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_fx8oeyj9q,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_gdugh5fgp,
    inst_evh_8564662037462133_close,
    inst_evh_7789867021953041_commandEventName_pwitilws5,
    inst_evh_7789867021953041_success_aifj42nun,
    inst_evh_7789867021953041_commandEventName_3q20sm0ak,
    loadEventData: inst_evh_7789867021953041_commandEventName_3q20sm0ak,
    inst_evh_7789867021953041_commandEventName,
  };

  const evh_7315092382398562_close_dzisd8cs3 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_dzisd8cs3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_fx8oeyj9q = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_fx8oeyj9q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_gdugh5fgp = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_gdugh5fgp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_commandEventName_pwitilws5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7789867021953041_commandEventName_pwitilws5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_success_aifj42nun = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_success_aifj42nun.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_commandEventName_3q20sm0ak = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7789867021953041_commandEventName_3q20sm0ak.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_commandEventName = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_commandEventName.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="EntityEvents"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" class="h-100" v-if="loaded">
      <div class="row h-100">
        <div class="col-6 h-100">
          <VueSlickgrid
            name="events"
            :headerTitle="pgettext('EntityEvents', 'Events')"
            :items="ds"
            checkbox-row-selection
            :headerShowColumnSelectorButton="
              b_7789867021953041_headerShowColumnSelectorButton
            "
            :headerShowReloadButton="b_7789867021953041_headerShowReloadButton"
            export-to-csv
            :exportToCsvFilename="pgettext('ExportFilename', 'payments')"
            :columns="b_7789867021953041_columns"
            @command:event_name="
              evh_7789867021953041_commandEventName($event, {})
            "
          >
          </VueSlickgrid>
        </div>
        <div class="col-6 h-100 overflow-hidden">
          <ItpCodeMirror
            mode="json"
            :options="b_2070266589801014_options"
            v-model="eventData"
            :value="eventData"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (eventData =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            class="h-100 v-100"
          >
          </ItpCodeMirror>
        </div>
      </div>
    </UcBody>
  </ItpPage>
</template>
